import React, {useCallback, useId, useMemo} from 'react';
import {CornerSize, ViewMode} from '@taopix/taopix-core';
import {
  Button,
  ListViewIcon,
  GridViewIcon,
  SinglePageViewWithThumbsIcon,
  EditBoxIcon,
  SquareIcon,
  SelectList,
  MatchesMediaQuery,
  PreviewIcon,
  ButtonWithPopout,
  ChevronDownSmallIcon,
} from '@taopix/taopix-design-system';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import classNames from 'classnames';
import {useExperience} from '../Experience/ExperienceProvider';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {useTranslation} from 'react-i18next';
import {useTaopix} from '../Context/TaopixContext';
import {ListItemDataType} from '@taopix/taopix-design-system/dist/types/Components/SelectList/SelectList';
import {useSelector} from 'react-redux';
import {LayoutSelector} from '../../Selectors';
import useViewMode from '../../Hooks/useViewMode';

const ViewModeSelector = ({
  buttonProps,
  showAsDropDown,
}: {
  buttonProps?: Partial<ButtonProps>;
  showAsDropDown?: 'auto' | boolean;
}) => {
  const app = useTaopix();
  const ui = useUiState();
  const {experience} = useExperience();
  const [viewMode, setViewMode] = useViewMode();
  const {t, i18n} = useTranslation(['main']);
  const layout = useSelector(LayoutSelector);
  const compactMode =
    showAsDropDown === 'auto' ? MatchesMediaQuery('(max-width:1000px)') : showAsDropDown;
  const id = useId().replace(new RegExp(':', 'g'), '');

  enum ExperienceCodes {
    'list' = 'ls',
    'grid' = 'gv',
    'single' = 'spv',
  }

  const onSetViewMode = useCallback(
    (newMode: ViewMode) => {
      if (ViewMode.Grid === newMode && ui.state.zoomLevel > 100) {
        ui.dispatch(UserInterfaceActions.setZoomLevel(100));
      }
      setViewMode(newMode);
    },
    [ui.state.zoomLevel]
  );

  const changeViewMode = (newViewMode: ViewMode) => {
    // If any toolpalettes are dirty prevent viewmode from switching
    if (ui.state.palette.dirty) {
      return ui.dispatch(
        UserInterfaceActions.setToolPalette({
          requestedPath: newViewMode,
          confirmationRequired: true,
        })
      );
    }

    // Reset the zoom level
    app.core.get('abilities').setZoom(100, false, false);
    ui.dispatch(UserInterfaceActions.setZoomLevel(100));
    // Trigger the callback function
    onSetViewMode(newViewMode);
  };

  const buttonStyleProps: ButtonProps = {
    hideLabel: ui.state.isSmallScreen,
    buttonStyle: 'standard',
    size: ui.state.isSmallScreen ? 'medium' : 'small',
    labelLines: 1,
    corners: 'square',
    ...buttonProps,
  };

  const enabledViewModes = [
    experience.EDITOR.workArea.editingViewModes.list,
    experience.EDITOR.workArea.editingViewModes.grid,
    experience.EDITOR.workArea.editingViewModes.page,
    experience.EDITOR.workArea.previewMode.includePreviewOption,
  ].filter(Boolean);

  const viewModeCount = enabledViewModes.length;

  const oneEditingView =
    viewModeCount === 2 && experience.EDITOR.workArea.previewMode.includePreviewOption;

  const viewModes = useMemo(() => {
    const items: Array<ListItemDataType> = [];

    if (experience.EDITOR.workArea.editingViewModes.list) {
      items.push({
        label: oneEditingView
          ? t('*:str_ButtonEditingView')
          : t('str_ButtonListView', {ns: 'main'}),
        value: ViewMode.List,
        icon: oneEditingView ? <EditBoxIcon /> : <ListViewIcon />,
      });
    }

    if (experience.EDITOR.workArea.editingViewModes.grid) {
      items.push({
        label: oneEditingView
          ? t('*:str_ButtonEditingView')
          : t('str_ButtonGridView', {ns: 'main'}),
        value: ViewMode.Grid,
        icon: oneEditingView ? <EditBoxIcon /> : <GridViewIcon />,
      });
    }

    if (experience.EDITOR.workArea.editingViewModes.page) {
      items.push({
        label: oneEditingView
          ? t('*:str_ButtonEditingView')
          : t('str_ButtonSinglePageView', {ns: 'main'}),
        value: ViewMode.Single,
        icon: oneEditingView ? (
          <EditBoxIcon />
        ) : experience.EDITOR.workArea.singlePage.pageThumbnailBar ? (
          <SinglePageViewWithThumbsIcon />
        ) : (
          <SquareIcon />
        ),
      });
    }

    if (experience.EDITOR.workArea.previewMode.includePreviewOption) {
      items.push({
        label: t('str_ButtonPreviewView', {ns: 'main'}),
        value: ViewMode.Preview,
        icon: <PreviewIcon />,
      });
    }

    return items;
  }, [i18n.language]);

  const selectedViewMode = useMemo(() => {
    return viewModes.find(item => item.value === viewMode);
  }, [viewMode]);

  // Do not show the view mode selector if there is only one view, or while the editor is zoomed
  if (viewModeCount === 1 || ui.state.zoomLevel > 100) {
    return <></>;
  }

  return (
    <>
      {compactMode && viewModeCount > 1 ? (
        // Drop down list version
        <ButtonWithPopout
          type="listbox"
          returnFocusToTriggerOnItemClick
          buttonProps={{
            id: 'viewModeSelectorDroplist-' + id,
            label: selectedViewMode?.label,
            startIcon: selectedViewMode?.icon,
            endIcon: <ChevronDownSmallIcon />,
            hideLabel: true,
            buttonStyle: 'standard',
            size: 'small',
            justified: true,
            className: 'items-center',
            corners: 'square',
            ...buttonProps,
          }}
        >
          {viewModes.map(mode => {
            return (
              <Button
                key={mode.value}
                id={'viewModeSelectorButton-' + mode.value + '-' + id}
                label={mode.label}
                startIcon={mode.icon}
                aria-pressed={viewMode === mode.value}
                onClick={() => changeViewMode(mode.value as ViewMode)}
                {...buttonStyleProps}
                className={classNames(
                  experience.EDITOR.workArea.defaultView === ExperienceCodes[mode.value] &&
                    'order-first'
                )}
              />
            );
          })}
        </ButtonWithPopout>
      ) : (
        // Button bar version
        <>
          {viewModes.map(mode => {
            return (
              <Button
                key={mode.value}
                id={'viewModeSelectorButton-' + mode.value + '-' + id}
                label={mode.label}
                startIcon={mode.icon}
                aria-pressed={viewMode === mode.value}
                onClick={() => changeViewMode(mode.value as ViewMode)}
                {...buttonStyleProps}
                className={classNames(
                  experience.EDITOR.workArea.defaultView === ExperienceCodes[mode.value] &&
                    'order-first'
                )}
              />
            );
          })}
        </>
      )}
    </>
  );
};

export default ViewModeSelector;
